import { clearAuthState, setAuthState } from "@/lib/auth-store";
import { refreshKeycloakSession } from "@/lib/auth/keycloak";

interface SessionAuthData {
  accessToken?: string;
  idToken?: string;
  preferredUsername?: string;
  expiresAt?: number;
}

const AUTH_REDIRECT_LOCK_KEY = "auth_redirect_in_progress_at";
const AUTH_REDIRECT_LOCK_TTL_MS = 15_000;
export const AUTH_LOGOUT_INTENT_STORAGE_KEY = "auth_logout_intent";
export const AUTH_EXPIRED_EVENT = "bpm-auth-expired";

export function beginAuthRedirect(): boolean {
  const browserWindow = globalThis.window;
  if (!browserWindow) {
    return false;
  }

  try {
    const now = Date.now();
    const rawValue = browserWindow.sessionStorage.getItem(AUTH_REDIRECT_LOCK_KEY);
    const lastRedirectAt = rawValue ? Number(rawValue) : 0;

    if (Number.isFinite(lastRedirectAt) && now - lastRedirectAt < AUTH_REDIRECT_LOCK_TTL_MS) {
      return false;
    }

    browserWindow.sessionStorage.setItem(AUTH_REDIRECT_LOCK_KEY, String(now));
    return true;
  } catch {
    return true;
  }
}

export function clearAuthRedirectLock(): void {
  const browserWindow = globalThis.window;
  if (!browserWindow) {
    return;
  }

  try {
    browserWindow.sessionStorage.removeItem(AUTH_REDIRECT_LOCK_KEY);
  } catch {
  }
}

export function markLogoutIntent(): void {
  const browserWindow = globalThis.window;
  if (!browserWindow) {
    return;
  }

  try {
    browserWindow.localStorage.setItem(AUTH_LOGOUT_INTENT_STORAGE_KEY, "1");
  } catch {
  }
}

export function clearLogoutIntent(): void {
  const browserWindow = globalThis.window;
  if (!browserWindow) {
    return;
  }

  try {
    browserWindow.localStorage.removeItem(AUTH_LOGOUT_INTENT_STORAGE_KEY);
  } catch {
  }
}

export function hasLogoutIntent(): boolean {
  const browserWindow = globalThis.window;
  if (!browserWindow) {
    return false;
  }

  try {
    return browserWindow.localStorage.getItem(AUTH_LOGOUT_INTENT_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function syncAuthStateWithSession(session: SessionAuthData | null | undefined): boolean {
  if (!session?.accessToken) {
    clearAuthState();
    return false;
  }

  setAuthState({
    accessToken: session.accessToken,
    preferredUsername: session.preferredUsername ?? null,
    idToken: session.idToken ?? null,
    expiresAt: session.expiresAt ?? null,
  }, { sessionConfirmed: true });
  return true;
}

export async function syncAuthStateFromSession(minimumValiditySeconds = 60): Promise<boolean> {
  if (!globalThis.window) {
    return false;
  }
  return refreshKeycloakSession(minimumValiditySeconds);
}

export function notifyAuthExpired(
  reason: string,
  endpoint: string,
  sessionRecoveryExhausted = false,
): void {
  globalThis.dispatchEvent?.(
    new CustomEvent(AUTH_EXPIRED_EVENT, {
      detail: { reason, endpoint, sessionRecoveryExhausted },
    }),
  );
}
